// src/App.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import './index.css';
import ScrollToTop from './components/ScrollToTop';
import ReactPixel from 'react-facebook-pixel';
import { track } from './utils/analytics';
import { useState, useEffect } from 'react';

import Navbar    from './components/Navbar';
import Hero      from './components/Hero';
import Footer    from './components/Footer';

import Tienda            from './pages/tienda/Tienda';
import Producto          from './pages/tienda/Producto';
import Carrito           from './pages/tienda/Carrito';
import Checkout          from './pages/tienda/Checkout';
import PedidoOk          from './pages/tienda/PedidoOk';
import NuestraHistoria   from './pages/tienda/NuestraHistoria';
import CompromisoAmbiental from './pages/tienda/CompromisoAmbiental';
import SistemaRefill     from './pages/tienda/SistemaRefill';
import Contacto          from './pages/tienda/Contacto';
import Noticias          from './pages/tienda/Noticias';
import NoticiaDetalle    from './pages/tienda/NoticiaDetalle';
import MiCuenta          from './pages/tienda/MiCuenta';
import PoliticaPrivacidad  from './pages/tienda/PoliticaPrivacidad';
import ResetPassword       from './pages/tienda/ResetPassword';
import Pqrs                from './pages/tienda/Pqrs';
import TerminosCondiciones from './pages/tienda/TerminosCondiciones';
import Landing              from './pages/tienda/Landing';

import Login         from './pages/admin/Login';
import Dashboard     from './pages/admin/Dashboard';
import Productos     from './pages/admin/Productos';
import Pedidos       from './pages/admin/Pedidos';
import Inventario    from './pages/admin/Inventario';
import Finanzas      from './pages/admin/Finanzas';
import Facturacion   from './pages/admin/Facturacion';
import Clientes      from './pages/admin/Clientes';
import Resenas       from './pages/admin/Resenas';
import Cupones       from './pages/admin/Cupones';
import NoticiasAdmin from './pages/admin/NoticiasAdmin';
import LandingPagesAdmin from './pages/admin/LandingPagesAdmin';
import Configuracion from './pages/admin/Configuracion';
import PqrsAdmin     from './pages/admin/PqrsAdmin';
import Analytics     from './pages/admin/Analytics';
import Categorias    from './pages/admin/Categorias';
import AdminLayout   from './components/AdminLayout';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'100vh' }}>
      <div className="spinner" />
    </div>
  );
  return user?.rol === 'admin' ? children : <Navigate to="/admin/login" replace />;
}

function PublicLayout({ children, showHero = false, showFooter = true }) {
  return (
    <>
      <Navbar />
      {showHero ? <Hero /> : <div style={{ height: 76 }} />}
      {children}
      {showFooter && <Footer />}
    </>
  );
}

function Mantenimiento() {
  return (
    <div style={{ minHeight:'100vh', background:'#9FBE6A', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div style={{ textAlign:'center' }}>
        <img src="https://res.cloudinary.com/dcbd2vdzn/image/upload/v1777340903/logo_wawita2_tge5t4.png" width="120" style={{ borderRadius:'50%', marginBottom:24 }} alt="Wawita" />
        <h1 style={{ fontFamily:'var(--font-display)', color:'#1C3A1E', fontSize:32, marginBottom:12 }}>Estamos mejorando para ti 🌿</h1>
        <p style={{ color:'#1C3A1E', fontSize:16, opacity:0.8 }}>Volvemos muy pronto con novedades.<br/>Escríbenos al WhatsApp +57 311 340 7979</p>
      </div>
    </div>
  );
}

function Proximamente() {
  return (
    <div style={{ minHeight:'100vh', background:'#FDFBF7', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div style={{ textAlign:'center' }}>
        <img src="https://res.cloudinary.com/dcbd2vdzn/image/upload/v1777340903/logo_wawita2_tge5t4.png" width="120" style={{ borderRadius:'50%', marginBottom:24 }} alt="Wawita" />
        <h1 style={{ fontFamily:'var(--font-display)', color:'var(--forest)', fontSize:32, marginBottom:12 }}>Próximamente 🌿</h1>
        <p style={{ color:'var(--ink-light)', fontSize:16 }}>Estamos preparando algo especial.<br/>Escríbenos al WhatsApp +57 311 340 7979</p>
        <a href="https://wa.me/573113407979" style={{ display:'inline-block', marginTop:24, padding:'12px 32px', background:'#9FBE6A', color:'#1C3A1E', borderRadius:12, fontWeight:700, textDecoration:'none' }}>WhatsApp</a>
      </div>
    </div>
  );
}

export default function App() {
  const [modoSitio, setModoSitio] = useState('activo');
  const [modoLoading, setModoLoading] = useState(true);

  useEffect(() => {
    ReactPixel.init('1880068542660907', {}, { autoConfig: true, debug: false });
    ReactPixel.pageView();
  }, []);

  useEffect(() => {
    // Leer modo desde la BD via API
    fetch('/api/config/estado-sitio', { cache: 'no-store' })
      .then(r => r.ok ? r.json() : { modo: 'activo' })
      .then(d => { setModoSitio(d.modo || 'activo'); setModoLoading(false); })
      .catch(() => { setModoSitio('activo'); setModoLoading(false); });
  }, []);

  function PixelPageView() {
    const location = useLocation();
    useEffect(() => {
      ReactPixel.pageView();
      track('page_view', { pagina: location.pathname });
    }, [location.pathname]);
    return null;
  }

  if (modoLoading) return null;

  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <PixelPageView />
          <Toaster position="top-right" toastOptions={{ style:{ fontFamily:'var(--font-body)', fontWeight:600, fontSize:14 } }} />
          <Routes>
            <Route path="/" element={<PublicLayout showHero={true}><Tienda /></PublicLayout>} />
            <Route path="/producto/:id"         element={<PublicLayout><Producto /></PublicLayout>} />
            <Route path="/carrito"              element={<PublicLayout><Carrito /></PublicLayout>} />
            <Route path="/checkout"             element={<PublicLayout><Checkout /></PublicLayout>} />
            <Route path="/pedido-ok"            element={<PublicLayout><PedidoOk /></PublicLayout>} />
            <Route path="/nuestra-historia"     element={<PublicLayout><NuestraHistoria /></PublicLayout>} />
            <Route path="/compromiso-ambiental" element={<PublicLayout><CompromisoAmbiental /></PublicLayout>} />
            <Route path="/sistema-refill"       element={<PublicLayout><SistemaRefill /></PublicLayout>} />
            <Route path="/contacto"             element={<PublicLayout><Contacto /></PublicLayout>} />
            <Route path="/noticias"             element={<PublicLayout><Noticias /></PublicLayout>} />
            <Route path="/noticias/:slug"       element={<PublicLayout><NoticiaDetalle /></PublicLayout>} />
            <Route path="/landing/:slug"        element={<PublicLayout showFooter={false}><Landing /></PublicLayout>} />
            <Route path="/mi-cuenta"            element={<PublicLayout><MiCuenta /></PublicLayout>} />
            <Route path="/privacidad"           element={<PublicLayout><PoliticaPrivacidad /></PublicLayout>} />
            <Route path="/terminos"             element={<PublicLayout><TerminosCondiciones /></PublicLayout>} />
            <Route path="/reset-password"       element={<PublicLayout><ResetPassword /></PublicLayout>} />
            <Route path="/pqrs"                  element={<PublicLayout><Pqrs /></PublicLayout>} />
            <Route path="/admin/login" element={<Login />} />
            <Route path="/admin" element={<PrivateRoute><AdminLayout /></PrivateRoute>}>
              <Route index                 element={<Dashboard />} />
              <Route path="productos"      element={<Productos />} />
              <Route path="pedidos"        element={<Pedidos />} />
              <Route path="inventario"     element={<Inventario />} />
              <Route path="finanzas"       element={<Finanzas />} />
              <Route path="facturacion"    element={<Facturacion />} />
              <Route path="clientes"       element={<Clientes />} />
              <Route path="resenas"        element={<Resenas />} />
              <Route path="cupones"        element={<Cupones />} />
              <Route path="noticias"       element={<NoticiasAdmin />} />
              <Route path="landing-pages"  element={<LandingPagesAdmin />} />
              <Route path="configuracion"  element={<Configuracion />} />
              <Route path="pqrs"          element={<PqrsAdmin />} />
              <Route path="analytics"     element={<Analytics />} />
              <Route path="categorias"     element={<Categorias />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}
